import { formatDateDB } from '@fortress-technology-solutions/fortress-component-library/utils';
import { parseCalendarDateUtc } from '../../utils/date-helpers';
import {
  EDIT_PAYLOAD_KEYS,
  FUNDING_TYPE_OPTIONS,
  NYC_SET_ASIDE_OPTION,
  SET_ASIDE_OPTIONS,
} from './constants';
import type { Building } from '../ManageBuildings/types';
import type { EditBuildingFormValues, Gates, ParsedBuilding } from './types';

export const formatBuildingAddress = ({
  streetAddress1,
  city,
  state,
  zipCode,
}: Building): string =>
  `${streetAddress1}, ${city}, ${state?.split('-')[1] ?? state} ${zipCode}`;

export const getFundingTypeLabel = (value: ?string): ?string =>
  FUNDING_TYPE_OPTIONS.find((option) => option.value === value)?.text ?? null;

export const getConstructionTypeOptions = (intl: Object, messages: Object) => [
  { value: 'REHAB', text: intl.formatMessage(messages.rehab) },
  {
    value: 'NEW_CONSTRUCTION',
    text: intl.formatMessage(messages.newConstruction),
  },
];

export const getSetAsideOptions = ({
  state,
  city,
}: {
  state: ?string,
  city: ?string,
}) => [
  ...SET_ASIDE_OPTIONS,
  {
    ...NYC_SET_ASIDE_OPTION,
    disabled: !(
      state === 'US-NY' && city?.toLowerCase().startsWith('new york')
    ),
  },
];

export const parseBuildings = ({
  buildings,
  yesLabel,
  noLabel,
  constructionTypeOptions,
}: {
  buildings: Building[],
  yesLabel: string,
  noLabel: string,
  constructionTypeOptions: { value: string, text: string }[],
}): ParsedBuilding[] =>
  buildings.map((building) => ({
    ...building,
    buildingAddress: formatBuildingAddress(building),
    unitsCount:
      typeof building.unitsCount === 'number' ? building.unitsCount : null,
    heraSpecialLabel: building.heraSpecial ? yesLabel : noLabel,
    fundingTypeLabel: getFundingTypeLabel(building.fundingType),
    constructionTypeLabel:
      constructionTypeOptions.find(
        ({ value }) => value === building.constructionType,
      )?.text ?? null,
  }));

export const toNumberOrNull = (value: mixed): ?number =>
  value === null || value === undefined || value === '' ? null : Number(value);

const emptyToNull = (value: ?string): ?string => {
  const trimmed = typeof value === 'string' ? value.trim() : value;
  return trimmed === '' || trimmed == null ? null : trimmed;
};

export const toFormValues = (building: Building): EditBuildingFormValues => ({
  streetAddress1: building.streetAddress1 ?? '',
  city: building.city ?? '',
  state: building.state ?? '',
  zipCode: building.zipCode ?? '',
  buildingIDNumber: building.buildingIDNumber ?? '',
  placedInServiceDate: parseCalendarDateUtc(building.placedInServiceDate),
  XMLSiteName: building.XMLSiteName ?? '',
  heraSpecial: Boolean(building.heraSpecial),
  minimumSetAside: building.minimumSetAside ?? '',
  RDSiteId: building.RDSiteId ?? '',
  PHBuildingNumber: building.PHBuildingNumber ?? '',
  PHBuildingEntrance: building.PHBuildingEntrance ?? '',
  fundingType: building.fundingType ?? '',
  applicableFractionUnit: building.applicableFractionUnit ?? '',
  applicableFractionFloorSpace: building.applicableFractionFloorSpace ?? '',
  certificateOfOccupancyDate: parseCalendarDateUtc(
    building.certificateOfOccupancyDate,
  ),
  constructionType: building.constructionType ?? '',
});

/**
 * Same field rules as the V1 inline row (keep them aligned until V1 is removed). FES-1531:
 * Joi rejects '' for RDSiteId, so a blank value goes as null; both fraction keys are sent
 * whenever naur is on, stored values passed through when the fields are hidden, because the
 * backend compares a missing key with Number(undefined).
 */
export const buildUpdatePayload = ({
  values,
  building,
  gates,
}: {
  values: EditBuildingFormValues,
  building: Building,
  gates: Gates,
}): Object => {
  const {
    isRDProperty,
    isPHProperty,
    naur,
    showFractions,
    showFundingType,
    showCertOccConstructionType,
  } = gates;

  const payload = {
    streetAddress1: values.streetAddress1.trim(),
    city: values.city.trim(),
    state: values.state,
    zipCode: values.zipCode,
    buildingIDNumber: (values.buildingIDNumber ?? '').trim(),
    placedInServiceDate: values.placedInServiceDate || null,
    XMLSiteName: emptyToNull(values.XMLSiteName),
    heraSpecial: Boolean(values.heraSpecial),
    fundingType: showFundingType ? values.fundingType || null : null,
    minimumSetAside: values.minimumSetAside ?? '',
    ...(isRDProperty ? { RDSiteId: emptyToNull(values.RDSiteId) } : {}),
    ...(isPHProperty
      ? {
          PHBuildingNumber: (values.PHBuildingNumber ?? '').trim(),
          PHBuildingEntrance: (values.PHBuildingEntrance ?? '').trim(),
        }
      : {}),
    ...(naur
      ? {
          applicableFractionUnit: showFractions
            ? toNumberOrNull(values.applicableFractionUnit)
            : toNumberOrNull(building.applicableFractionUnit),
          applicableFractionFloorSpace: showFractions
            ? toNumberOrNull(values.applicableFractionFloorSpace)
            : toNumberOrNull(building.applicableFractionFloorSpace),
        }
      : {}),
    ...(showCertOccConstructionType
      ? {
          // Date-only column: keep the picked local day, no UTC shift
          certificateOfOccupancyDate:
            formatDateDB(values.certificateOfOccupancyDate, false) || null,
          constructionType: values.constructionType || null,
        }
      : {}),
  };

  return Object.fromEntries(
    Object.entries(payload).filter(([key]) => EDIT_PAYLOAD_KEYS.includes(key)),
  );
};

export const fractionsChanged = (
  building: Building,
  payload: Object,
): boolean =>
  ['applicableFractionUnit', 'applicableFractionFloorSpace'].some(
    (key) =>
      key in payload &&
      toNumberOrNull(payload[key]) !== toNumberOrNull(building[key]),
  );

// Fail safe: an unknown NAUR state blocks fraction edits, as the V1 row did.
export const hasUnresolvedNaurs = ({
  naur,
  isLoading,
  isError,
  naurEvents,
}: {
  naur: boolean,
  isLoading: boolean,
  isError: boolean,
  naurEvents: ?Array<{ status: string }>,
}): boolean => {
  if (!naur) return false;
  if (isLoading || isError) return true;
  if (!naurEvents?.length) return false;
  return naurEvents.some((event) => event.status !== 'SATISFIED');
};
